import { ColorCircle } from "@/src/components/colorCircle";
import { DayPicker } from "@/src/components/datePicker";
import PageHeader from "@/src/components/pageHeader";
import { AppointmentType, Patient } from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import { uid } from "@/src/utils/utils";

import moment from "moment";
import { useRef, useState } from "react";

import { AppointmentsCalendar } from "./appointmentsCalendar";
import { CalendarUsersList } from "./calendarUsersList";
import FullCalendar from "@fullcalendar/react";
import { Button } from "@/src/components/buttons";
import CalendarViewType from "./calendarViewType";
moment.updateLocale("fr", {
  week: {
    dow: 0,
  },
});

function CalendarPage({
  patient,
  userId,
  onDateSelect,
}: {
  patient?: Patient | null | undefined;
  userId: string | null | undefined;
  onDateSelect?: (start: string, end: string, doctorId: string) => void;
}) {
  const [viewType, setViewType] = useState<"day" | "week" | "month">("week");
  const [reloadUsersList, setReloadUsersList] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<
    string | null | undefined
  >(userId || USER.get()?.id);
  const [start, setStart] = useState<string>(
    moment().startOf("week").format("YYYY-MM-DD")
  );
  const [end, setEnd] = useState<string>(
    moment().endOf("week").format("YYYY-MM-DD")
  );
  const calendarRef = useRef<FullCalendar>(null);
  let [searchKey, setSearchKey] = useState("");
  let [selectedAppointmentTypes, setSelectedAppointmentTypes] = useState<
    (AppointmentType | null | undefined)[]
  >(USER.get()?.service?.appointmentTypes || []);

  return (
    <div
      id="calendarPage"
     
    >
      <CalendarUsersList
        key={`reloadUsersList-${reloadUsersList}`}
        start={start}
        end={end}
        current={selectedUserId}
        appointmentsTypes={selectedAppointmentTypes}
        onChange={(newUserId) => {
          setSelectedUserId(newUserId);
        }}
      />
      <div
        className="fullCalendar p20"
        style={{ height: "100%", width: "100%", overflow: "hidden" }}
      >
        <div className="c3 mc1 gap15 acenter">
          <PageHeader
            key={uid()}
            title="Agenda"
            subTitle="Gérez les rendez-vous"
          />
          <div className="center mc1 gap10 ">
            <DayPicker
              value={moment(start || null).format("YYYY-MM-DD")}
              increment={viewType == "day" ? 1 : 7}
              onChange={async (newValue) => {
                if (viewType == "day") {
                  setStart(moment(newValue).format("YYYY-MM-DD"));
                  setEnd(moment(newValue).add(1, "day").format("YYYY-MM-DD"));
                } else {
                  setStart(
                    moment(newValue).startOf("week").format("YYYY-MM-DD")
                  );
                  setEnd(moment(newValue).endOf("week").format("YYYY-MM-DD"));
                }
                setReloadUsersList((prev) => prev + 1);
                (calendarRef.current as any)
                  ?.getApi()
                  .gotoDate(moment(newValue).format("YYYY-MM-DD"));
              }}
            ></DayPicker>
          </div>
          <div>
            <CalendarViewType
              current={viewType}
              onChange={(newValue) => {
                setViewType(newValue);
                setReloadUsersList((prev) => prev + 1);
                if (newValue == "day") {
                  setStart(moment().format("YYYY-MM-DD"));
                  setEnd(moment().format("YYYY-MM-DD"));
                } else {
                  setStart(moment(start).startOf("week").format("YYYY-MM-DD"));
                  setEnd(moment(end).endOf("week").format("YYYY-MM-DD"));
                }
              }}
            ></CalendarViewType>
          </div>
        </div>

        <AppointmentsCalendar
          key={uid()}
          viewType={viewType}
          calendarRef={calendarRef}
          start={start}
          end={end}
          searchKey={searchKey}
          selectedUserId={selectedUserId}
          selectedAppointmentTypes={selectedAppointmentTypes}
          patient={patient}
          onDateSelect={onDateSelect}
          onAppointmentChange={(appointment) => {
            setReloadUsersList((prev) => prev + 1);
          }}
        />
      </div>
      <CalendarLegends
        selected={selectedAppointmentTypes}
        onChange={(newAppointmentTypes) => {
          setSelectedAppointmentTypes(newAppointmentTypes);
        }}
      ></CalendarLegends>
    </div>
  );
}

function CalendarLegends({
  selected,
  onChange,
}: {
  selected?: (AppointmentType | null | undefined)[];
  onChange?: (newValue: (AppointmentType | null | undefined)[]) => void;
}) {
  let [value, setValue] = useState(selected || []);

  function allAreActive() {
    for (let type of USER.get()?.service?.appointmentTypes || []) {
      if (!type) continue;
      if (!value.find((t) => t?.id == type.id)) return false;
    }
    return true;
  }

  return (
    <div
      className="card grid c1 gap10 p15 bs double msingle"
      style={{ padding: "15px" }}
    >
      <div className="grid c5 mc3 gap10">
        <AllLegendsChip
          key={uid()}
          isActive={allAreActive()}
          onClick={() => {
            let newValue = allAreActive()
              ? []
              : USER.get()?.service?.appointmentTypes || [];
            setValue(newValue);
            onChange?.(newValue);
          }}
        />
        {USER.get()?.service?.appointmentTypes?.map((type) => {
          if (!type) return;
          let isActive = value?.find((t) => t?.id == type.id);
          return (
            <div
              key={uid()}
              className="pointer grid c1 gap10"
              onClick={() => {
                let newValue = value?.filter((t) => t?.id != type.id);
                if (!newValue) return;
                if (isActive) {
                  setValue(newValue);
                  onChange?.(newValue);
                  return;
                }
                newValue?.push(type);
                setValue(newValue);
                onChange?.(newValue);
              }}
            >
              <div
                className="cauto-1 acenter gap10"
                style={{ color: isActive ? "" : "RGBA(0,0,0,0.3)" }}
              >
                <ColorCircle
                  color={isActive ? type.color : "RGBA(0,0,0,0.3)"}
                ></ColorCircle>
                {type.name}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AllLegendsChip({
  isActive,
  onClick,
}: {
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <div key={uid()} className="pointer grid c1 gap10" onClick={onClick}>
      <div
        className="cauto-1 acenter gap10"
        style={{ color: isActive ? "" : "RGBA(0,0,0,0.3)" }}
      >
        <ColorCircle
          color={isActive ? "black" : "RGBA(0,0,0,0.3)"}
        ></ColorCircle>
        Tous
      </div>
    </div>
  );
}

export { CalendarPage };
